import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Field, type Column } from "@fawkes/ui";

import { formatDate } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-4): the read-only Ashes/urn-return overview — the React port of the Jmix
// as-retour view. Lists the dossiers whose ashes are ready for return (status
// AS_URN_KLAAR_VOOR_RETOUR), longest-waiting first. Read-only; ROLE_CREMATORIUM_VIEW is
// server-enforced.
//
// The url is RELATIVE ("crematorium/as-retour") — the dataProvider prepends /api once; a leading
// slash would double to /api/api → 404. Search is client-side over the fetched list.

interface AsRetourRow {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  eigenaarNaam: string | null;
  asUrnNummer: string | null;
  retourwijze: string | null;
  asKlaarSinds: string | null;
}

export function CrematoriumAsRetourList() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<AsRetourRow[]>({
    url: "crematorium/as-retour",
    method: "get",
  });
  const rows = data?.data ?? [];

  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.dossierNummer ?? ""} ${r.eigenaarNaam ?? ""} ${r.dierNaam ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const columns: Column<AsRetourRow>[] = [
    { header: t("crematoriumAsRetour.col.dossier"), cell: (r) => r.dossierNummer ?? "—" },
    { header: t("crematoriumAsRetour.col.dier"), cell: (r) => r.dierNaam ?? "—" },
    { header: t("crematoriumAsRetour.col.eigenaar"), cell: (r) => r.eigenaarNaam ?? "—" },
    { header: t("crematoriumAsRetour.col.asUrnNummer"), cell: (r) => r.asUrnNummer ?? "—" },
    { header: t("crematoriumAsRetour.col.retourwijze"), cell: (r) => r.retourwijze ?? "—" },
    {
      header: t("crematoriumAsRetour.col.klaarSinds"),
      cell: (r) => formatDate(r.asKlaarSinds, locale),
    },
  ];

  return (
    <div className="product-overview">
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <Field
            label={t("crematoriumAsRetour.search.label")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="asiel-tarief__toolbar-spacer" />
          <Button variant="secondary" onClick={() => refetch()}>
            {t("crematoriumAsRetour.action.refresh")}
          </Button>
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumAsRetour.load.error")}
          </div>
        )}

        <IndeelbareTabel<AsRetourRow> tabelKey="crematorium-as-retour-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("crematoriumAsRetour.empty")}
        />
      </div>
    </div>
  );
}
